import { useCallback, useContext, useEffect, useRef } from 'react';
import { Model, ModelService } from '../services';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { useModelStore } from '../../../stores/modelStore';
import { PrescriptionModelItem } from './PrescriptionModelItem';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { TrackerEventService } from '../../prescription/trackerEventService';
import { DialogWrappedComponentProps, FeedbackContext } from '../../../providers/FeedbackProvider';
import HighlightedModels from './Dialog/HighlightedModels';
import { get } from 'lodash';
import { DateTime } from 'luxon';
import { PrescriberService } from '../../../shared/prescriber';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { logger } from '../../../helpers/logger';
import { useSearchTypeStore } from '../../../stores/searchTypeStore';
import { SEARCH_TYPE } from '../../../components/search/SearchTypeEnum';
import { amplitudeModule } from '../../../shared/amplitude';

export function PrescriptionModels() {
  const { onConfirmModelInsertion, models, sponsoredModels } = useModelStore();
  const { prescription, showedHighlightedModels } = usePrescriptionStore();
  const { openBaseDialog } = useContext(FeedbackContext);
  const { prescriberConfiguration } = usePrescriptionStore();
  const prescriber = usePrescriptionStore().prescriber();
  const { searchType } = useSearchTypeStore();

  const isMobile = useIsMobile();
  const eventTriggeredRef = useRef(false);
  const prescriberConfigRef = useRef(prescriberConfiguration);
  prescriberConfigRef.current = prescriberConfiguration;

  const prescriptionItemsAmount = usePrescriptionStore((store) => store.prescriptionItemsAmount());
  const enableSponsoredModel = get(prescriberConfiguration, 'Features.enableSponsoredModel', false);

  const onSelectModel = useCallback(
    async (model: Model) => {
      if (!prescription) return;

      const modelDetails = await ModelService.fetchModelDetails(model.id, prescription.idPrescricao, prescription.Prescritor.idPrescritor);

      amplitudeModule.searchResultClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        validation_code: prescription.CodigoValidacao ?? '',
        prescription_id: prescription.idPrescricao ?? 0,
        screen_name: 'modelos',
        search_type: 'Modelo',
        search_term: '',
        protocol_id: model.id ?? 0,
        protocol_name: model.nome ?? '',
      });

      await onConfirmModelInsertion(model.id, modelDetails, true);

      if (model.destacado) {
        try {
          await TrackerEventService.track(prescription.idPrescricao, 'Modelo_inserido_item_patrocinado', {
            idPrescricao: prescription.idPrescricao,
            idPrescricaoModelo: model.id,
            idMedico: prescription.Prescritor.idPrescritor,
            idMedicamento: modelDetails.Medicamento.map((medicine) => medicine.IdMedicamento),
            isMobile,
          });

          await TrackerEventService.track(prescription?.idPrescricao, 'backoffice_prescription_models_view_clicou', {
            idMedico: prescriber?.idPrescritor,
            idPrescricao: prescription?.idPrescricao,
            idParceiro: prescriberConfiguration?.idParceiro,
            codigoValidacao: prescription.CodigoValidacao,
            tipoModelo: 1,
            idPrescricaoModelo: model.id,
            idMedicamento: modelDetails.Medicamento.map((medicine) => medicine.IdMedicamento),
            Timestamp: DateTime.now(),
            isMobile,
          });
        } catch (error) {
          // Silently fail tracking to not break the flow
        }
      }
    },
    [onConfirmModelInsertion, prescription, isMobile, prescriber, prescriberConfiguration]
  );

  const handleHighlightedModelEvents = useCallback(
    async (event: string, extraProps?: any) => {
      try {
        if (!prescription?.idPrescricao) return;

        await TrackerEventService.track(prescription?.idPrescricao, event, {
          idMedico: prescriber?.idPrescritor,
          idPrescricao: prescription?.idPrescricao,
          idParceiro: prescriberConfiguration?.idParceiro,
          codigoValidacao: prescription.CodigoValidacao,
          tipoModelo: extraProps?.tipoModelo || 2,
          Timestamp: DateTime.now(),
          isMobile,
          ...extraProps,
        });
      } catch (error) {}
    },
    [
      prescriber?.idPrescritor,
      prescriberConfiguration?.idParceiro,
      prescription?.idPrescricao,
      prescription?.CodigoValidacao,
      isMobile,
    ]
  );

  const handleSponsoredModelsConfig = useCallback(
    async (
      operation: 'reset' | 'update' | 'none' = 'none',
      highlightCounterExists: boolean = false,
      counter: number | null = null
    ): Promise<Array<{ chave: string; valor: number | Date }>> => {
      const configs = prescriber?.Configuracoes;

      let counterShowedTimes = highlightCounterExists ? counter : configs?.showSponsoredModel ?? 0;
      let resetTimerDate = configs?.sponsoredModelLastShowed ? new Date(configs.sponsoredModelLastShowed) : null;

      const now = new Date();
      const resetConst = parseInt(process.env.REACT_APP_SPONSORED_RESET_HOURS || '24', 10);
      const resetTimeInMilliseconds = resetConst * 60 * 60 * 1000;
      const diff = resetTimerDate ? now.getTime() - resetTimerDate.getTime() : 0;

      const reachedThresholdWithoutTimer =
        typeof counterShowedTimes === 'number' && counterShowedTimes >= 2 && !resetTimerDate;
      const resetTimerExpired = diff > resetTimeInMilliseconds;

      if (!highlightCounterExists && (reachedThresholdWithoutTimer || resetTimerExpired)) {
        operation = 'reset';
        counterShowedTimes = 0;
        resetTimerDate = null;
      }

      if (operation === 'update') {
        if (typeof counterShowedTimes === 'number') {
          counterShowedTimes += 1;
          resetTimerDate = counterShowedTimes === 2 ? now : null;
        }
      }

      if (operation !== 'none') {
        if (prescription?.Prescritor?.idPrescritor) {
          try {
            // Por algum motivo, preciso chamar a api sequencialmente, do contrário não atualiza corretamente
            await PrescriberService.updateDoctorPreferences(prescription?.Prescritor?.idPrescritor, {
              chave: 'showSponsoredModel',
              valor: counterShowedTimes,
            });
            await PrescriberService.updateDoctorPreferences(prescription?.Prescritor?.idPrescritor, {
              chave: 'sponsoredModelLastShowed',
              valor: resetTimerDate || '',
            });
          } catch (error) {
            logger.error('Error updating doctor preferences:', error);
          }
        }
      }

      return [
        { chave: 'showSponsoredModel', valor: counterShowedTimes as number },
        { chave: 'sponsoredModelLastShowed', valor: resetTimerDate ?? 0 },
      ];
    },
    [prescriber?.Configuracoes, prescription?.Prescritor?.idPrescritor]
  );

  useEffect(() => {
    let currentCounter = 0;
    const run = async () => {
      if (sponsoredModels?.length === 0) return;
      if (showedHighlightedModels) return;

      const [counter] = (await handleSponsoredModelsConfig()) as { chave: string; valor: number }[];

      if (typeof counter.valor === 'number' && counter.valor >= 2) return;
      currentCounter = counter.valor || 0;

      if (!enableSponsoredModel) return;

      handleHighlightedModelEvents('backoffice_prescription_models_view', {
        idPrescricaoModelo: sponsoredModels.map((model) => model.id),
        tipoModelo: 2,
      });

      amplitudeModule.popUpViewed({
        partner_id: prescriberConfigRef.current?.idParceiro ?? 0,
        partner_name: prescriberConfigRef.current?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        pop_up_type: 'modelos patrocinados',
        pop_up_message: 'Facilite o preenchimento da receita com modelos.',
      });

      let closedByAction = false;

      return openBaseDialog(
        (dialogProps: DialogWrappedComponentProps) => (
          <HighlightedModels
            onClose={() => {
              closedByAction = true;
              dialogProps.onClose();
            }}
            onConfirm={() => {
              closedByAction = true;
              dialogProps.onClose();
            }}
            handleHighlightedModelEvents={handleHighlightedModelEvents}
            handleSponsoredModelsConfig={handleSponsoredModelsConfig}
            sponsoredCounter={currentCounter}
          />
        ),
        {
          width: '600px',
          height: 'none',
          backgroundColor: 'transparent',
          persist: false,
          overflow: 'auto',
          onOverlayClickCallback: () => {
            if (!closedByAction) {
              amplitudeModule.popUpClicked({
                pop_up_id: 1,
                pop_up_type: 'modelos patrocinados',
                pop_up_title: 'Modelos',
                pop_up_content: 'Facilite o preenchimento da receita com modelos.',
                pop_up_dismiss: true,
                pop_up_later: false,
                pop_up_agreed: false,
                partner_id: prescriberConfigRef.current?.idParceiro ?? 0,
                partner_name: prescriberConfigRef.current?.Nome ?? '',
                establishment_name: prescription?.Estabelecimento?.Nome ?? '',
              });
            }
          },
        }
      );
    };

    if (models.length >= 1) {
      if (!eventTriggeredRef.current) {
        const highlightedModels = models.filter((model) => model.destacado);
        if (highlightedModels.length >= 1) {
          handleHighlightedModelEvents('backoffice_prescription_models_view', {
            idPrescricaoModelo: highlightedModels.map((model) => model.id),
            tipoModelo: 1,
          });
          eventTriggeredRef.current = true;
          return;
        }
      }
    }

    run();
  }, [
    enableSponsoredModel,
    handleHighlightedModelEvents,
    sponsoredModels,
    openBaseDialog,
    showedHighlightedModels,
    prescriber,
    prescription,
    handleSponsoredModelsConfig,
    models,
  ]);

  // Mostra a barra de modelos se:
  // 1. Não há itens prescritivos (prescriptionItemsAmount === 0),
  // E há modelos disponíveis
  const shouldShowModels = prescriptionItemsAmount === 0 && models?.length > 0;

  if (!shouldShowModels) return null;

  return (
    <Box data-testid={'prescription-models-list'}>
      <Text fontSize={'12px'} fontWeight={600} color={'typography.main'}>
        Modelos
      </Text>

      <Flex w={'100%'} overflow={'auto'} gap={'5px'}>
        {models?.map((model: Model) => (
          <PrescriptionModelItem model={model} key={model.id} onSelectModel={onSelectModel} />
        ))}
      </Flex>
    </Box>
  );
}
